import { create } from "zustand";

/**
 * Card audio mini-player — same idea as the old shared HTML <audio>:
 *
 * - ONE persistent Shaka engine lives in CardAudioMiniPlayer
 * - Works for ALL formats (mp3, aac, m3u8, …)
 * - Changing pages does NOT destroy it → no restart
 * - Only Play on another track loads a new URL
 * - Other video/YouTube/audio dismisses it
 */

export type CardAudioShakaMiniPlayerMeta = {
  title?: string;
  artist?: string;
  artwork?: string;
  src?: string;
  startTime?: number;
  showMiniPlayer?: boolean;
};

export type CardAudioShakaMiniPlayerState = {
  visible: boolean;
  src: string;
  title: string;
  artist: string;
  artwork: string;
  playing: boolean;
  currentTime: number;
  duration: number;
  showMiniPlayer: boolean;
  /** Bumps when user wants play after load (engine reads this). */
  playRequestId: number;
};

type Actions = {
  /** Open mini + set track. Same src keeps the engine (no restart). */
  playTrack: (meta: CardAudioShakaMiniPlayerMeta) => void;
  setPlayback: (partial: {
    playing?: boolean;
    currentTime?: number;
    duration?: number;
  }) => void;
  setShowMiniPlayer: (show: boolean) => void;
  hide: () => void;
};

const EMPTY: CardAudioShakaMiniPlayerState = {
  visible: false,
  src: "",
  title: "",
  artist: "",
  artwork: "",
  playing: false,
  currentTime: 0,
  duration: 0,
  showMiniPlayer: false,
  playRequestId: 0,
};

export const useCardAudioShakaMiniPlayerStore = create<CardAudioShakaMiniPlayerState & Actions>(
  (set, get) => ({
    ...EMPTY,
    playTrack: (meta) => {
      const nextSrc = String(meta.src ?? "").trim();
      if (!nextSrc) return;

      const prev = get();
      const sameTrack = isSameShakaAudioSrc(prev.src, nextSrc);

      set({
        visible: true,
        src: nextSrc,
        title: String(meta.title ?? "").trim() || "Now playing",
        artist: String(meta.artist ?? "").trim(),
        artwork: String(meta.artwork ?? "").trim(),
        playing: true,
        showMiniPlayer: meta.showMiniPlayer !== false,
        // Keep position when same track (resume / toggle); reset only on new track.
        currentTime: sameTrack ? prev.currentTime : Math.max(0, Number(meta.startTime) || 0),
        duration: sameTrack ? prev.duration : 0,
        playRequestId: prev.playRequestId + 1,
      });
    },
    setPlayback: (partial) =>
      set((state) => ({
        playing: partial.playing ?? state.playing,
        currentTime:
          partial.currentTime !== undefined
            ? Math.max(0, Number(partial.currentTime) || 0)
            : state.currentTime,
        duration:
          partial.duration !== undefined
            ? Math.max(0, Number(partial.duration) || 0)
            : state.duration,
      })),
    setShowMiniPlayer: (show) => set({ showMiniPlayer: show }),
    hide: () => set(EMPTY),
  })
);

export function isSameShakaAudioSrc(a: string, b: string) {
  if (!a || !b) return false;
  try {
    return new URL(a, window.location.href).href === new URL(b, window.location.href).href;
  } catch {
    return a === b;
  }
}

export function showCardAudioShakaMiniPlayer(meta: CardAudioShakaMiniPlayerMeta) {
  useCardAudioShakaMiniPlayerStore.getState().playTrack(meta);
}

export function playCardShakaAudio(meta: CardAudioShakaMiniPlayerMeta) {
  useCardAudioShakaMiniPlayerStore.getState().playTrack(meta);
}

export function dismissCardAudioShakaMiniPlayer() {
  // Clear live media FIRST so in-flight Shaka audio play() callbacks cannot
  // call pauseEveryoneElse and tear down / reset an active video mini-player.
  const media = getShakaAudioLiveMedia();
  clearShakaAudioLiveMedia();
  if (media) {
    media.pause();
  }
  useCardAudioShakaMiniPlayerStore.getState().hide();
}

/** Pause without closing the mini player (e.g. another video started). */
export function pauseCardShakaAudio() {
  const media = getShakaAudioLiveMedia();
  if (media && !media.paused) {
    media.pause();
  }
  useCardAudioShakaMiniPlayerStore.getState().setPlayback({ playing: false });
}

export function isCardAudioShakaMiniPlayerVisible() {
  return useCardAudioShakaMiniPlayerStore.getState().visible;
}

// ----- Persistent live media element (owned by mini player's engine) -----

let liveMedia: HTMLVideoElement | null = null;
const liveListeners = new Set<() => void>();

function notifyLive() {
  liveListeners.forEach((fn) => fn());
}

export function setShakaAudioLiveMedia(media: HTMLVideoElement | null) {
  liveMedia = media;
  notifyLive();
}

export function getShakaAudioLiveMedia() {
  return liveMedia;
}

export function clearShakaAudioLiveMedia() {
  if (!liveMedia) return;
  liveMedia = null;
  notifyLive();
}

export function subscribeShakaAudioLiveMedia(listener: () => void) {
  liveListeners.add(listener);
  return () => {
    liveListeners.delete(listener);
  };
}
